/**
 * Shared building blocks for the Departments pages, built from the component library and
 * following the Locations / Tasks patterns (page header, clickable stat tiles, titled panels,
 * inline error notices, person cells).
 */

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconAlertCircle, IconArrowLeft, IconLock } from '@tabler/icons-react';
import { Alert, Badge, Box, Button, Card, Group, Modal, SimpleGrid, Skeleton, Stack, Text, TextInput, ThemeIcon, Title, Tooltip } from '@/components/ui';
import { Avatar, Divider } from '@/components/ui/data-display';
import { errorMessage, initials } from './departmentsUtils';

// ---------------------------------------------------------------- page header

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  backTo?: string;
  backLabel?: string;
}

export function PageHeader({ title, description, actions, backTo, backLabel = 'Back' }: PageHeaderProps) {
  return (
    <Stack gap="xs">
      {backTo && (
        <Group>
          <Button variant="subtle" size="compact-sm" leftSection={<IconArrowLeft size={14} />} component={Link} to={backTo}>
            {backLabel}
          </Button>
        </Group>
      )}
      <Group justify="space-between" wrap="wrap" align="flex-start" gap="sm">
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Title order={2}>{title}</Title>
          {description && (
            <Text c="dimmed" size="sm">
              {description}
            </Text>
          )}
        </Stack>
        {actions && (
          <Group gap="sm" wrap="wrap">
            {actions}
          </Group>
        )}
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------- stat tile

interface StatTileProps {
  icon: ReactNode;
  color: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  active?: boolean;
  /** Coloured left rail for things that need attention. */
  emphasis?: boolean;
  onClick?: () => void;
}

export function StatTile({ icon, color, label, value, hint, active, emphasis, onClick }: StatTileProps) {
  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        backgroundColor: active ? `var(--mantine-color-${color}-light)` : undefined,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {(active || emphasis) && (
        <Box style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, backgroundColor: `var(--mantine-color-${color}-6)` }} />
      )}
      <Group wrap="nowrap" gap="md" align="center">
        <ThemeIcon size={44} radius="md" variant={active ? 'filled' : 'light'} color={color} style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lineClamp={1}>
            {label}
          </Text>
          <Text size="xl" fw={700} lh={1.2}>
            {value}
          </Text>
          {hint && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {hint}
            </Text>
          )}
        </Stack>
      </Group>
    </Card>
  );
}

export function StatTilesSkeleton({ count = 4 }: { count?: number }) {
  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: count }}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} height={86} radius="md" />
      ))}
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------- section card

interface SectionCardProps {
  icon?: ReactNode;
  color?: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  noDivider?: boolean;
  padding?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  style?: CSSProperties;
}

export function SectionCard({ icon, color = 'blue', title, description, actions, children, noDivider, padding = 'lg', style }: SectionCardProps) {
  return (
    <Card shadow="sm" padding={padding} radius="md" withBorder style={style}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm" mb={noDivider ? 'sm' : 'xs'}>
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
          {icon && (
            <ThemeIcon size="lg" radius="md" variant="light" color={color} style={{ flexShrink: 0 }}>
              {icon}
            </ThemeIcon>
          )}
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Title order={4}>{title}</Title>
            {description && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        </Group>
        {actions && (
          <Group gap="xs" wrap="wrap">
            {actions}
          </Group>
        )}
      </Group>
      {!noDivider && <Divider mb="md" />}
      {children}
    </Card>
  );
}

// ---------------------------------------------------------------- badges

/** Small neutral or coloured badge with normal casing. */
export function Tag({ color = 'gray', children, icon, tooltip, variant = 'light' }: { color?: string; children: ReactNode; icon?: ReactNode; tooltip?: string; variant?: 'light' | 'outline' | 'filled' | 'dot' }) {
  const badge = (
    <Badge size="sm" variant={variant} color={color} leftSection={icon} styles={{ root: { textTransform: 'none' } }}>
      {children}
    </Badge>
  );
  return tooltip ? (
    <Tooltip label={tooltip} withArrow multiline maw={280}>
      <span>{badge}</span>
    </Tooltip>
  ) : (
    badge
  );
}

export function ActiveBadge({ isActive, size = 'sm' }: { isActive: boolean; size?: 'xs' | 'sm' | 'md' }) {
  return (
    <Badge size={size} variant="light" color={isActive ? 'green' : 'gray'} styles={{ root: { textTransform: 'none' } }}>
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

// ---------------------------------------------------------------- feedback

/** Inline failure: the server message under a short title. */
export function ErrorNotice({ error, title = 'That did not work', onRetry }: { error: unknown; title?: string; onRetry?: () => void }) {
  if (!error) return null;
  return (
    <Alert color="red" icon={<IconAlertCircle size={16} />} title={title} action={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}>
      {errorMessage(error)}
    </Alert>
  );
}

/** Shown in place of a section the user has no right to see. */
export function LockedNotice({ title = 'No access', children }: { title?: string; children: ReactNode }) {
  return (
    <Card withBorder radius="md" padding="lg">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color="gray" size="lg" radius="md">
          <IconLock size={18} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={600}>{title}</Text>
          <Text size="sm" c="dimmed">
            {children}
          </Text>
        </Stack>
      </Group>
    </Card>
  );
}

// ---------------------------------------------------------------- people

interface PersonCellProps {
  name: string;
  subtitle?: ReactNode;
  avatar?: string | null;
  muted?: boolean;
  size?: 'sm' | 'md';
  color?: string;
}

export function PersonCell({ name, subtitle, avatar, muted, size = 'sm', color = 'blue' }: PersonCellProps) {
  return (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      <Avatar src={avatar || undefined} radius="xl" size={size === 'sm' ? 32 : 40} color={color}>
        {initials(name)}
      </Avatar>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="sm" fw={600} c={muted ? 'dimmed' : undefined} lineClamp={1}>
          {name}
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {subtitle}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

/** Label / value pair for detail cards. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Box style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{children}</Box>
    </Stack>
  );
}

// ---------------------------------------------------------------- confirm

interface ConfirmActionModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  /** Mantine colour of the confirm button (filled, so the colour shows). */
  color?: string;
  loading?: boolean;
  error?: unknown;
  /** When set, the confirm button stays disabled until this text is typed (case-insensitive). */
  typeToConfirm?: string | null;
  confirmDisabled?: boolean;
}

/**
 * Confirmation for deactivate / activate / remove actions. Shows the failure inline (the caller
 * toasts it too) and can ask the user to type a name before a risky change.
 */
export function ConfirmActionModal({ opened, onClose, onConfirm, title, children, confirmLabel, color = 'red', loading, error, typeToConfirm, confirmDisabled }: ConfirmActionModalProps) {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (opened) setTyped('');
  }, [opened]);

  const needsTyping = !!typeToConfirm;
  const typedOk = !needsTyping || typed.trim().toLowerCase() === (typeToConfirm ?? '').trim().toLowerCase();

  return (
    <Modal opened={opened} onClose={loading ? () => undefined : onClose} title={title} size="md" centered>
      <Stack gap="md">
        {children}
        {needsTyping && (
          <TextInput
            label={
              <Text span size="sm">
                Type <Text span fw={700}>{typeToConfirm}</Text> to confirm
              </Text>
            }
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            autoComplete="off"
            data-autofocus
          />
        )}
        <ErrorNotice error={error} />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="filled" color={color} onClick={onConfirm} loading={loading} disabled={!typedOk || confirmDisabled}>
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
